import { Popover } from "@mui/material";
import { Code as CodeIcon, X as XIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives/Button";
import { usePopupState } from "@/shared/hooks/usePopupState";
import type { SearchQueryBody } from "@/types";

import type { RoboqlMenuConfig } from "../FilterModel";
import { RoboqlFilterMenu } from "../RoboqlFilterMenu";

import styles from "./ActiveFilters.module.css";
import roboqlStyles from "./RoboqlActiveFilters.module.css";

export interface RoboqlActiveFiltersProps {
  /** The active roboql query string — pre-fills the editor when the
   *  user clicks the chip body to edit. */
  query: string;
  /** Fires when the user clicks `×` on the chip. The hook collapses the
   *  state to `"none"` regardless of the index argument (roboql is
   *  single-valued); we pass `0` for symmetry with the structured
   *  variant's `onRemove(index)` signature. */
  onRemove: (index: number) => void;
  /** Fires from the Clear button on the right. Drops all state. */
  onClear: () => void;
  /** Rendered in the trailing group, ahead of Clear. */
  trailingActions?: React.ReactNode;
  /** Fires when the editor commits via Execute. The parent hook routes
   *  this through `view.commit({ type: "roboql", query, spec })`. */
  onExecute: (query: string, spec: SearchQueryBody) => void;
  /** Config for the chip-edit popover (target + autocomplete context). */
  roboql: RoboqlMenuConfig;
}

/**
 * Chip-row variant rendered when `filters.type === "roboql"`. Single
 * "RoboQL filter applied" chip, plus a click-to-edit popover that
 * mounts `<RoboqlFilterMenu>` directly (no mode bar). Different from
 * the outer `<ResourceView.Filters>` trigger, which uses the
 * mode-switching `<FilterMenu>` shell — when the user already has an
 * active RoboQL filter, the chip-edit gesture is "open the editor on
 * this query," not "switch query mode."
 */
export function RoboqlActiveFilters({
  query,
  onRemove,
  onClear,
  trailingActions,
  onExecute,
  roboql,
}: RoboqlActiveFiltersProps) {
  const editPopup = usePopupState();

  const handleEditClick = (event: React.MouseEvent<HTMLElement>) => {
    if (editPopup.isOpen) {
      editPopup.close();
    } else {
      editPopup.open({ anchorEl: event.currentTarget });
    }
  };

  return (
    <div className={styles.bar} role="region" aria-label="Active filters">
      <RoboqlChip
        onEdit={handleEditClick}
        onRemove={() => onRemove(0)}
        editOpen={editPopup.isOpen}
      />
      <Popover
        open={editPopup.isOpen}
        anchorEl={editPopup.anchor}
        onClose={editPopup.close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            className: styles.popover,
            role: "dialog",
            "aria-label": "Edit RoboQL filter",
          },
        }}
      >
        <RoboqlFilterMenu
          initialValue={query}
          target={roboql.target}
          onExecute={(next, spec) => {
            onExecute(next, spec);
            editPopup.close();
          }}
          onCancel={editPopup.close}
        />
      </Popover>
      <div className={styles.trailing}>
        {trailingActions}
        <Button variant="text" size="small" color="inherit" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

interface RoboqlChipProps {
  onEdit: (event: React.MouseEvent<HTMLElement>) => void;
  onRemove: () => void;
  /** Whether the edit popover is currently open — drives `aria-expanded`
   *  on the chip-body button. */
  editOpen: boolean;
}

/**
 * Minimal v1 chip — fixed label "RoboQL filter applied" + a `×`. The
 * design shows the full query text inline; we ship a placeholder for
 * v1 with UX refinement (truncated query, multi-line on hover, syntax
 * highlighting) tracked in Section 7.3 of the plan.
 *
 * Co-located in this file because it's purely a render helper for
 * this one variant — no other consumer renders it. If a second
 * surface ever needs it, promote to a sibling file.
 */
function RoboqlChip({ onEdit, onRemove, editOpen }: RoboqlChipProps) {
  return (
    <span className={roboqlStyles.chip}>
      <button
        type="button"
        className={roboqlStyles.chipBody}
        onClick={onEdit}
        aria-haspopup="dialog"
        aria-expanded={editOpen}
        aria-label="Edit RoboQL filter"
      >
        <CodeIcon className={roboqlStyles.chipIcon} aria-hidden="true" />
        <span className={roboqlStyles.chipLabel}>RoboQL filter applied</span>
      </button>
      <button
        type="button"
        className={roboqlStyles.chipRemove}
        onClick={onRemove}
        aria-label="Remove RoboQL filter"
      >
        <XIcon className={roboqlStyles.chipRemoveIcon} aria-hidden="true" />
      </button>
    </span>
  );
}
